"use client";

import { ProductDetail } from "@/components/product/product-detail";
import { RelatedProducts } from "@/components/product/related-products";
import {
  ProductErrorState,
  ProductLoadingState,
  ProductNotFoundState,
} from "@/components/ui/loading-states";
import { useProduct, type ValidProduct } from "@/hooks/use-product";
import { useOptimisticBasketActions } from "@/stores/basket-store";

type ProductContentProps = {
  productHandle: string;
};

/**
 * Main component with clean early returns for all edge cases
 */
export default function ProductContent({ productHandle }: ProductContentProps) {
  const {
    data: product,
    isLoading,
    error,
    isValidProduct: isValid,
  } = useProduct(productHandle);

  // Handle all non-success states early with dedicated components
  if (isLoading) return <ProductLoadingState />;
  if (error) return <ProductErrorState error={error} />;
  if (!(isValid && product)) return <ProductNotFoundState />;

  // At this point, TypeScript knows product is valid
  return <ValidProductDisplay product={product as ValidProduct} />;
}

/**
 * Main product display component - product is guaranteed to be valid here
 * Uses the new ProductDetail component following UI/UX specifications
 */
function ValidProductDisplay({ product }: { product: ValidProduct }) {
  const { addItem } = useOptimisticBasketActions();

  const handleAddToCart = async (
    productId: string,
    variantId: string,
    quantity: number
  ) => {
    try {
      // Find the variant to get pricing and details
      const variant = product.variants.find((v) => v.id === variantId);
      if (!variant) return;

      await addItem({
        id: productId,
        name: product.title,
        price: Number.parseFloat(
          variant.price?.amount || product.priceRange.minVariantPrice.amount
        ),
        images: product.images || [],
        brand: product.vendor,
        category: product.productType || undefined,
        handle: product.handle,
        variant: {
          id: variant.id,
          title: variant.title || "",
          price: variant.price || product.priceRange.minVariantPrice,
          selectedOptions: variant.selectedOptions || [],
        },
        quantity,
      });
    } catch {
      // Handle error silently or show user notification
    }
  };

  return (
    <div className="bg-white">
      <ProductDetail onAddToCart={handleAddToCart} product={product} />

      {/* Related Products Section */}
      <div className="mx-auto max-w-7xl px-4 py-16">
        <RelatedProducts currentProduct={product} />
      </div>
    </div>
  );
}
